import React, { useCallback, useMemo, useRef, useState } from "react";
import { Check, Clock, Copy, FileText, Loader2 } from "lucide-react";
import { logger } from "@amzn/afxtaco-utilities";
import MarkdownRenderer from "../MarkdownRenderer";

interface BlogPostTabContentProps {
  blogPost: {
    content: string | undefined;
    isLoading: boolean;
    isNotFound: boolean;
  };
  projectId: string;
}

// Average adult silent-reading speed for prose; used to turn the word count
// into a human-friendly "min read" estimate.
const WORDS_PER_MINUTE = 200;

/** Count whitespace-delimited words in the blog markdown. */
const countWords = (text: string): number => {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
};

const BlogPostTabContent: React.FC<BlogPostTabContentProps> = ({
  blogPost,
  projectId,
}) => {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Word count + read time derived from the raw markdown. Cheap, so it only
  // recomputes when the content changes.
  const { wordCount, readMinutes } = useMemo(() => {
    const words = blogPost.content ? countWords(blogPost.content) : 0;
    return {
      wordCount: words,
      readMinutes: Math.max(1, Math.ceil(words / WORDS_PER_MINUTE)),
    };
  }, [blogPost.content]);

  const handleCopy = useCallback(async () => {
    if (!blogPost.content) {
      return;
    }
    try {
      await navigator.clipboard.writeText(blogPost.content);
      setCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      // Clipboard write can fail in non-secure contexts or if permission is
      // denied — no user-facing indicator, the button just won't show "copied".
      logger.error({ loggerMsg: "Failed to copy blog post", error });
    }
  }, [blogPost.content]);

  if (blogPost.isLoading) {
    return (
      <div className="h-full overflow-y-auto bg-background">
        <div className="p-6 bg-background">
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-muted-foreground">
                Loading blog post...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blogPost.content) {
    return (
      <div className="h-full overflow-y-auto bg-background">
        <div className="p-6 bg-background">
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">
              No blog post available
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="p-6 bg-background">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              {wordCount.toLocaleString()} {wordCount === 1 ? "word" : "words"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {readMinutes} min read
            </span>
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Copy blog post markdown"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </button>
        </div>
        <MarkdownRenderer content={blogPost.content} projectId={projectId} />
      </div>
    </div>
  );
};

export default BlogPostTabContent;
